import BarChartIcon from "@mui/icons-material/BarChart";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Menu } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";

import { ConfirmationDialog } from "@/components/molecules/ConfirmationDialog";
import { CAMPAIGN_PERMISSIONS } from "@/constants/modules.permissions";
import ROUTE_URLS from "@/constants/route-urls";
import SWR_KEYS from "@/constants/swr-keys";
import useLanguage from "@/contexts/language-context";
import useRemoveCampaign from "@/hooks/requests/campaigns/use-remove-campaigns";
import useHasNoPermission from "@/hooks/use-check-permissions";
import useDialog from "@/hooks/use-dialog";
import useMutate from "@/hooks/use-mutate";
import { ICampaign } from "@/interfaces/campaign.interface";
import { CampaignsMessages } from "@/messages/campaigns/campaigns.messages";
import NotificationItem from "@/mui/Items/NotificationItem";

interface Props {
  isOpen: boolean;
  anchorEl: (EventTarget & HTMLButtonElement) | null;
  onClose: () => void;
  messages: CampaignsMessages;
  selected: ICampaign;
}

export function CampaignPopover({ isOpen, anchorEl, onClose, messages, selected }: Props) {
  const router = useNavigate();
  const { lang } = useLanguage();
  const { isVisible, handleOpen, handleClose } = useDialog<ICampaign>();
  const { removeCampaign, isLoading: isLoadingRemoveCampaign } = useRemoveCampaign();
  const { mutate } = useMutate();
  const _id = selected.id || 0;

  const hasNoPermissionToDelete = useHasNoPermission(CAMPAIGN_PERMISSIONS.DELETE);
  const hasNoPermissionToEdit = useHasNoPermission(CAMPAIGN_PERMISSIONS.EDIT);
  const hasNoPermissionToViewStats = useHasNoPermission(CAMPAIGN_PERMISSIONS.STATS);

  const onSuccess = () => {
    mutate(SWR_KEYS.getCampaigns);
    handleClose();
  };

  const handleConfirmDelete = async () => {
    await removeCampaign({ _id, onSuccess, successMessage: messages.deleteCampaignSuccess });
  };

  const handleViewStats = () => {
    router(ROUTE_URLS.campaignStatistics(lang, _id.toString()));
  };

  return (
    <Menu
      open={isOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
    >
      <NotificationItem
        disabled={hasNoPermissionToEdit}
        icon={<EditIcon />}
        onClick={() => router(ROUTE_URLS.updateCampaign(lang, _id.toString()))}
        title={messages.updatePopover}
      />

      <NotificationItem
        icon={<DeleteIcon />}
        disabled={hasNoPermissionToDelete}
        onClick={() => handleOpen(selected)}
        title={messages.removePopover}
      />

      <NotificationItem
        disabled={hasNoPermissionToViewStats}
        icon={<BarChartIcon />}
        onClick={handleViewStats}
        title={messages.statsPopover}
      />

      <ConfirmationDialog
        isOpen={isVisible}
        onClose={handleClose}
        onConfirm={() => handleConfirmDelete()}
        titleText={messages.deleteCampaignDialogTitle}
        descriptionText={messages.deleteCampaignDialogContent}
        cancelText={messages.deleteCampaignDialogCancel}
        isLoading={isLoadingRemoveCampaign}
        confirmText={messages.deleteCampaignDialogConfirm}
      />
    </Menu>
  );
}
